import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useIncidents } from "@/contexts/IncidentsContext";
import {
  Map as MapIcon,
  Radio,
  Cloud,
  Camera,
  Settings,
  LogOut,
  Sun,
  Moon,
  ShieldCheck,
  Volume2,
  VolumeX,
  ExternalLink,
  Plane,
  Train,
  History,
  MessageSquare,
  HeartPulse,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Role → page access. Keep this aligned with the backend matrix in server.py.
// "ops" = full dispatch experience; "free" = limited public-info pages only.
const OPS_ROLES = new Set(["dispatcher", "admin_dispatcher", "subscriber", "supervisor", "admin"]);
const ADMIN_ROLES = new Set(["admin_dispatcher", "admin"]);

const NAV = [
  { to: "/", label: "CAD", icon: MapIcon, key: "cad", access: "ops" },
  { to: "/active-incidents", label: "Active Incidents", icon: Radio, key: "active-incidents", access: "ops" },
  { to: "/weather", label: "Weather", icon: Cloud, key: "weather", access: "any" },
  { to: "/cameras", label: "Cameras", icon: Camera, key: "cameras", access: "any" },
  { to: "/atc", label: "ATC", icon: Plane, key: "atc", access: "any" },
  { to: "/nyct", label: "NYCT", icon: Train, key: "nyct", access: "any" },
  { to: "/ems-analytics", label: "EMS Analytics", icon: HeartPulse, key: "ems-analytics", access: "any" },
  { to: "/history", label: "History", icon: History, key: "history", access: "ops" },
  { to: "/chat", label: "Chat", icon: MessageSquare, key: "chat", access: "ops" },
  { to: "/admin", label: "Admin", icon: ShieldCheck, key: "admin", access: "admin" },
  { to: "/settings", label: "Settings", icon: Settings, key: "settings", access: "admin" },
];

function navAllowed(item, role) {
  if (item.access === "any") return true;
  if (item.access === "ops") return OPS_ROLES.has(role);
  if (item.access === "admin") return ADMIN_ROLES.has(role);
  return false;
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const { incidents, audioOn, setAudioEnabled, volume, setVolume, previewNotificationTone } = useIncidents();
  // Sidebar collapses to an off-canvas drawer below the `md` breakpoint
  // (< 768 px) so phones and small tablets aren't stuck with a 64px rail
  // eating half their content width.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const activeCount = incidents.filter((i) => ["pending", "dispatched", "on-scene"].includes(i.status)).length;
  const criticalCount = incidents.filter((i) => i.priority === "critical" && i.status !== "cleared").length;

  // Embed mode: when launched in a new tab via the "open in new tab" button, hide chrome.
  const embed = new URLSearchParams(location.search).get("embed") === "1";
  if (embed) {
    return (
      <div className="h-screen w-screen bg-background text-foreground overflow-hidden">
        <main className="absolute inset-0">{children}</main>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-screen flex flex-col bg-background text-foreground overflow-hidden">
      {/* Top bar — z-index intentionally above Leaflet map panes (which reach
          ~700) so the hamburger and controls stay tappable over any map. */}
      <header className="h-14 border-b border-border bg-card/80 backdrop-blur flex items-center px-2 sm:px-4 gap-2 sm:gap-4 z-[1100] shrink-0">
        <Button
          size="icon"
          variant="ghost"
          className="md:hidden -ml-1"
          onClick={() => setMobileNavOpen((v) => !v)}
          data-testid="mobile-nav-toggle"
          aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
        >
          {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
        <div className="flex items-center gap-2 min-w-0">
          <img src="/branding/psa-logo.png" alt="Public Safety Alerts" className="w-8 h-8 md:w-9 md:h-9 object-contain shrink-0" />
          <div className="font-bold tracking-[0.2em] text-xs sm:text-sm truncate">
            <span className="sm:hidden">PSA</span>
            <span className="hidden sm:inline">PUBLIC SAFETY ALERTS</span>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 sm:ml-4 font-mono text-xs">
          <Badge variant="outline" className="border-green-500/40 text-green-500" data-testid="status-system">
            ● SYSTEM ONLINE
          </Badge>
          <Badge
            variant="outline"
            className={`border-primary/40 ${criticalCount > 0 ? "text-priority-critical" : "text-primary"}`}
            data-testid="status-critical"
          >
            {criticalCount} CRITICAL
          </Badge>
          <Badge variant="outline" data-testid="status-active">
            {activeCount} ACTIVE
          </Badge>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <TooltipProvider>
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid="audio-toggle-btn"
                    >
                      {audioOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Notifications · {audioOn ? `${volume}%` : "MUTED"}</TooltipContent>
              </Tooltip>
              <PopoverContent align="end" className="w-72 p-4" data-testid="audio-popover">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Alert Tones</div>
                  <Switch
                    checked={audioOn}
                    onCheckedChange={(v) => setAudioEnabled(v)}
                    data-testid="audio-toggle-switch"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <VolumeX className="w-4 h-4 text-muted-foreground shrink-0" />
                  <Slider
                    value={[volume]}
                    min={0}
                    max={100}
                    step={5}
                    disabled={!audioOn}
                    onValueChange={([v]) => setVolume(v)}
                    data-testid="audio-volume-slider"
                    className="flex-1"
                  />
                  <Volume2 className="w-4 h-4 text-muted-foreground shrink-0" />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs font-mono text-muted-foreground" data-testid="audio-volume-value">
                    {audioOn ? `${volume}%` : "MUTED"}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!audioOn}
                    onClick={previewNotificationTone}
                    data-testid="audio-preview-btn"
                  >
                    Test tone
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="icon" variant="ghost" data-testid="theme-toggle-btn" onClick={toggle}>
                  {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{theme === "dark" ? "Light" : "Dark"} mode</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="font-mono text-xs px-3 py-1 border border-border rounded-sm hidden md:block" data-testid="user-info">
            <div className="text-muted-foreground text-[10px]">SIGNED IN</div>
            <div>{user?.name} · <span className="text-primary" data-testid="user-id-badge">{user?.username}</span></div>
          </div>
          <Button size="icon" variant="ghost" onClick={logout} data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile drawer backdrop — must sit above Leaflet map (z ~700) */}
        {mobileNavOpen && (
          <button
            aria-label="Close menu"
            onClick={() => setMobileNavOpen(false)}
            className="md:hidden fixed inset-0 z-[1150] bg-black/60 backdrop-blur-sm"
            data-testid="mobile-nav-backdrop"
          />
        )}
        {/* Sidebar — off-canvas drawer on mobile, static rail on md+, wide on lg.
            z-index bumped high enough to clear Leaflet panes/controls. */}
        <aside
          data-testid="sidebar"
          className={`fixed inset-y-14 left-0 z-[1200] w-64 border-r border-border bg-card flex flex-col py-2 transition-transform md:static md:inset-auto md:z-auto md:translate-x-0 md:w-16 lg:w-48 ${
            mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          }`}
        >
          {NAV.filter((n) => navAllowed(n, user?.role)).map((n) => {
            const Icon = n.icon;
            const active = (n.to === "/" && location.pathname === "/") || (n.to !== "/" && location.pathname.startsWith(n.to));
            return (
              <div
                key={n.key}
                className={`group flex items-stretch border-l-2 transition-colors ${
                  active
                    ? "border-primary bg-accent text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40"
                }`}
              >
                <Link
                  to={n.to}
                  data-testid={`nav-${n.key}`}
                  onClick={() => setMobileNavOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm flex-1 min-w-0"
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {/* Label visible in mobile drawer AND on lg+; hidden only on md-only rail. */}
                  <span className="md:hidden lg:inline tracking-widest text-xs uppercase truncate">{n.label}</span>
                </Link>
                <button
                  type="button"
                  data-testid={`nav-${n.key}-newtab`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(`${n.to}?embed=1`, "_blank", "noopener,noreferrer");
                  }}
                  title="Open in new tab"
                  className="hidden lg:flex items-center px-2 opacity-0 group-hover:opacity-100 transition-opacity hover:text-primary"
                >
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          <div className="mt-auto px-4 py-3 md:hidden lg:block font-mono text-[10px] text-muted-foreground border-t border-border">
            <div>{user?.agency}</div>
            <div className="mt-1">v1.0.0</div>
          </div>
        </aside>

        <main className="flex-1 overflow-hidden relative">{children}</main>
      </div>
    </div>
  );
}
